/**
 * Centralized API endpoints - aligned with backend OpenAPI spec
 */

export const API_BASE_URL = '/api';

export const endpoints = {
  taxYear: {
    config: '/tax-year',
    default: '/tax-year/default',
  },
  tenants: {
    list: '/tenants',
  },
  auth: {
    signup: '/auth/signup',
    login: '/auth/login',
    sendOtp: '/auth/send-otp',
    resendOtp: '/auth/resend-otp',
    verifyOtp: '/auth/verify-otp',
    verifyEmail: '/auth/verify-email',
    getRegistrationUserInfo: '/auth/get-registration-user-info',
    completeRegistration: '/auth/complete-registration',
    forgotPassword: '/auth/forgot-password',
    resetPassword: '/auth/reset-password',
  },
  profile: {
    me: '/me',
    updateProfile: '/me',
    updatePassword: '/me/password',
    logout: '/me/logout',
  },
  users: {
    roles: '/users/roles',
    list: '/users',
    create: '/users',
    bulk: '/users/bulk',
    getById: (userId: string) => `/users/${encodeURIComponent(userId)}`,
    update: (userId: string) => `/users/${encodeURIComponent(userId)}`,
    resendVerification: (userId: string) => `/users/${encodeURIComponent(userId)}/resend-verification`,
  },
  docs: {
    list: '/docs',
    upload: '/docs',
    getPreview: (documentId: string) => `/docs/${encodeURIComponent(documentId)}`,
    delete: (documentId: string) => `/docs/${encodeURIComponent(documentId)}`,
    confirmUpload: (documentId: string) =>
      `/docs/${encodeURIComponent(documentId)}/confirm-upload`,
    failUpload: (documentId: string) =>
      `/docs/${encodeURIComponent(documentId)}/fail-upload`,
  },
  adminDocs: {
    listByUser: (userId: string) => `/users/${encodeURIComponent(userId)}/docs`,
    /** Admin upload documents on behalf of a user (placeholder – integrate when backend ready) */
    uploadForUser: (userId: string) => `/users/${encodeURIComponent(userId)}/docs`,
    getPreview: (userId: string, documentId: string) =>
      `/users/${encodeURIComponent(userId)}/docs/${encodeURIComponent(documentId)}/preview`,
    confirmUpload: (userId: string, documentId: string) =>
      `/users/${encodeURIComponent(userId)}/docs/${encodeURIComponent(documentId)}/confirm-upload`,
    failUpload: (userId: string, documentId: string) =>
      `/users/${encodeURIComponent(userId)}/docs/${encodeURIComponent(documentId)}/fail-upload`,
  },
  settings: {
    taxFilingUploadEmails: '/settings/tax-filing-upload-emails',
  },
  taxSummaries: {
    list: '/tax-summaries',
    getPreview: (documentId: string) =>
      `/tax-summaries/${encodeURIComponent(documentId)}`,
    update: (documentId: string) =>
      `/tax-summaries/${encodeURIComponent(documentId)}`,
  },
  draftCopies: {
    list: '/draft-copies',
    getPreview: (documentId: string) =>
      `/draft-copies/${encodeURIComponent(documentId)}`,
    update: (documentId: string) =>
      `/draft-copies/${encodeURIComponent(documentId)}`,
  },
  declarations: {
    list: '/declarations',
    upload: '/declarations',
    getPreview: (documentId: string) =>
      `/declarations/${encodeURIComponent(documentId)}`,
    confirmUpload: (documentId: string) =>
      `/declarations/${encodeURIComponent(documentId)}/confirm-upload`,
    failUpload: (documentId: string) =>
      `/declarations/${encodeURIComponent(documentId)}/fail-upload`,
  },
  finalCopies: {
    list: '/final-copies',
    getPreview: (documentId: string) =>
      `/final-copies/${encodeURIComponent(documentId)}`,
    update: (documentId: string) =>
      `/final-copies/${encodeURIComponent(documentId)}`,
  },
  adminTaxSummaries: {
    listByUser: (userId: string) =>
      `/users/${encodeURIComponent(userId)}/tax-summaries`,
    createForUser: (userId: string) =>
      `/users/${encodeURIComponent(userId)}/tax-summaries`,
    getPreview: (userId: string, documentId: string) =>
      `/users/${encodeURIComponent(userId)}/tax-summaries/${encodeURIComponent(documentId)}`,
    update: (userId: string, documentId: string) =>
      `/users/${encodeURIComponent(userId)}/tax-summaries/${encodeURIComponent(documentId)}`,
    confirmUpload: (userId: string, documentId: string) =>
      `/users/${encodeURIComponent(userId)}/tax-summaries/${encodeURIComponent(documentId)}/confirm-upload`,
    failUpload: (userId: string, documentId: string) =>
      `/users/${encodeURIComponent(userId)}/tax-summaries/${encodeURIComponent(documentId)}/fail-upload`,
    delete: (userId: string, documentId: string) =>
      `/users/${encodeURIComponent(userId)}/tax-summaries/${encodeURIComponent(documentId)}`,
  },
  adminDraftCopies: {
    listByUser: (userId: string) =>
      `/users/${encodeURIComponent(userId)}/draft-copies`,
    createForUser: (userId: string) =>
      `/users/${encodeURIComponent(userId)}/draft-copies`,
    getPreview: (userId: string, documentId: string) =>
      `/users/${encodeURIComponent(userId)}/draft-copies/${encodeURIComponent(documentId)}`,
    update: (userId: string, documentId: string) =>
      `/users/${encodeURIComponent(userId)}/draft-copies/${encodeURIComponent(documentId)}`,
    confirmUpload: (userId: string, documentId: string) =>
      `/users/${encodeURIComponent(userId)}/draft-copies/${encodeURIComponent(documentId)}/confirm-upload`,
    failUpload: (userId: string, documentId: string) =>
      `/users/${encodeURIComponent(userId)}/draft-copies/${encodeURIComponent(documentId)}/fail-upload`,
    delete: (userId: string, documentId: string) =>
      `/users/${encodeURIComponent(userId)}/draft-copies/${encodeURIComponent(documentId)}`,
  },
  adminDeclarations: {
    listByUser: (userId: string) =>
      `/users/${encodeURIComponent(userId)}/declarations`,
    createForUser: (userId: string) =>
      `/users/${encodeURIComponent(userId)}/declarations`,
    getPreview: (userId: string, documentId: string) =>
      `/users/${encodeURIComponent(userId)}/declarations/${encodeURIComponent(documentId)}`,
    confirmUpload: (userId: string, documentId: string) =>
      `/users/${encodeURIComponent(userId)}/declarations/${encodeURIComponent(documentId)}/confirm-upload`,
    failUpload: (userId: string, documentId: string) =>
      `/users/${encodeURIComponent(userId)}/declarations/${encodeURIComponent(documentId)}/fail-upload`,
    delete: (userId: string, documentId: string) =>
      `/users/${encodeURIComponent(userId)}/declarations/${encodeURIComponent(documentId)}`,
  },
  adminFinalCopies: {
    listByUser: (userId: string) =>
      `/users/${encodeURIComponent(userId)}/final-copies`,
    createForUser: (userId: string) =>
      `/users/${encodeURIComponent(userId)}/final-copies`,
    getPreview: (userId: string, documentId: string) =>
      `/users/${encodeURIComponent(userId)}/final-copies/${encodeURIComponent(documentId)}`,
    update: (userId: string, documentId: string) =>
      `/users/${encodeURIComponent(userId)}/final-copies/${encodeURIComponent(documentId)}`,
    confirmUpload: (userId: string, documentId: string) =>
      `/users/${encodeURIComponent(userId)}/final-copies/${encodeURIComponent(documentId)}/confirm-upload`,
    failUpload: (userId: string, documentId: string) =>
      `/users/${encodeURIComponent(userId)}/final-copies/${encodeURIComponent(documentId)}/fail-upload`,
    delete: (userId: string, documentId: string) =>
      `/users/${encodeURIComponent(userId)}/final-copies/${encodeURIComponent(documentId)}`,
  },
  /** User: tax form instances (individual / business) */
  forms: {
    list: '/forms',
    create: '/forms',
    getById: (id: string) => `/forms/${encodeURIComponent(id)}`,
    update: (id: string) => `/forms/${encodeURIComponent(id)}`,
    delete: (id: string) => `/forms/${encodeURIComponent(id)}`,
  },
  /** Admin: tax forms for a specific user */
  adminForms: {
    listByUser: (userId: string) => `/users/${encodeURIComponent(userId)}/forms`,
    createForUser: (userId: string) => `/users/${encodeURIComponent(userId)}/forms`,
    getById: (userId: string, formId: string) =>
      `/users/${encodeURIComponent(userId)}/forms/${encodeURIComponent(formId)}`,
    update: (userId: string, formId: string) =>
      `/users/${encodeURIComponent(userId)}/forms/${encodeURIComponent(formId)}`,
    delete: (userId: string, formId: string) =>
      `/users/${encodeURIComponent(userId)}/forms/${encodeURIComponent(formId)}`,
  },
} as const;

export type Endpoints = typeof endpoints;
